import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { ShellComponent } from './layout/shell/shell.component';
import { LoginComponent } from './features/auth/login.component';
import { LandingComponent } from './features/landing/landing.component';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { ComplaintListComponent } from './features/complaints/complaint-list.component';
import { ComplaintSearchComponent } from './features/complaints/complaint-search.component';
import { ComplaintDetailComponent } from './features/complaints/complaint-detail.component';
import { WorkflowComponent } from './features/workflow/workflow.component';
import { CasesComponent } from './features/cases/cases.component';
import { SchedulingComponent } from './features/scheduling/scheduling.component';
import { RosterComponent } from './features/roster/roster.component';
import { ResolutionComponent } from './features/resolution/resolution.component';
import { HearingManagementComponent } from './features/hearing-management/hearing-management.component';
import { NotificationsComponent } from './features/notifications/notifications.component';
import { ReportsComponent } from './features/reports/reports.component';
import { SettingsComponent } from './features/settings/settings.component';
import { RolesComponent } from './features/roles/roles.component';
import { ResourcesComponent } from './features/resources/resources.component';
import { ProfileComponent } from './features/profile/profile.component';
import { DocumentOcrShellComponent } from './features/document-ocr/document-ocr-shell.component';
import { OcrCaptureComponent } from './features/document-ocr/ocr-capture.component';
import { OcrDocumentsComponent } from './features/document-ocr/ocr-documents.component';
import { OcrReviewComponent } from './features/document-ocr/ocr-review.component';
import { AssistantComponent } from './features/assistant/assistant.component';

export const routes: Routes = [
  {
    // Public front door — shown to everyone, signed in or not. Bouncing an
    // existing session straight to /dashboard made the site open on a desk page
    // instead of the landing page.
    path: '',
    pathMatch: 'full',
    component: LandingComponent,
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    component: LoginComponent,
  },
  {
    path: '',
    canActivate: [authGuard],
    component: ShellComponent,
    children: [
      { path: 'dashboard', component: DashboardComponent, canActivate: [roleGuard], data: { module: 'dashboard' } },
      {
        path: 'assistant',
        component: AssistantComponent,
        canActivate: [roleGuard],
        data: { module: 'assistant' },
      },
      {
        path: 'complaints',
        component: ComplaintListComponent,
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        // Must come before 'complaints/:id' — otherwise the router greedily matches
        // 'search' as an :id param and routes here to the detail page instead.
        path: 'complaints/search',
        component: ComplaintSearchComponent,
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        path: 'complaints/:id',
        component: ComplaintDetailComponent,
        canActivate: [roleGuard],
        data: { module: 'complaints' },
      },
      {
        path: 'workflow',
        component: WorkflowComponent,
        canActivate: [roleGuard],
        data: { module: 'workflow' },
      },
      {
        path: 'cases',
        component: CasesComponent,
        canActivate: [roleGuard],
        data: { module: 'cases' },
      },
      {
        path: 'scheduling',
        component: SchedulingComponent,
        canActivate: [roleGuard],
        data: { module: 'scheduling' },
      },
      { path: 'hearings', redirectTo: 'hearing-management', pathMatch: 'full' },
      {
        path: 'hearing-management',
        component: HearingManagementComponent,
        canActivate: [roleGuard],
        data: { module: 'hearingManagement' },
      },
      {
        path: 'roster',
        component: RosterComponent,
        canActivate: [roleGuard],
        data: { module: 'roster' },
      },
      {
        path: 'resolution',
        component: ResolutionComponent,
        canActivate: [roleGuard],
        data: { module: 'resolution' },
      },
      { path: 'resolutions', redirectTo: 'resolution', pathMatch: 'full' },
      {
        path: 'notifications',
        component: NotificationsComponent,
        canActivate: [roleGuard],
        data: { module: 'notifications' },
      },
      {
        path: 'reports',
        component: ReportsComponent,
        canActivate: [roleGuard],
        data: { module: 'reports' },
      },
      { path: 'analytics', redirectTo: 'reports', pathMatch: 'full' },
      {
        path: 'document-ocr',
        component: DocumentOcrShellComponent,
        canActivate: [roleGuard],
        data: { module: 'documentOcr' },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'capture' },
          { path: 'capture', component: OcrCaptureComponent },
          { path: 'documents', component: OcrDocumentsComponent },
          { path: 'review/:id', component: OcrReviewComponent },
        ],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [roleGuard],
        data: { module: 'settings' },
      },
      {
        path: 'roles',
        component: RolesComponent,
        canActivate: [roleGuard],
        data: { module: 'roles' },
      },
      {
        path: 'resources',
        component: ResourcesComponent,
        canActivate: [roleGuard],
        data: { module: 'roles' },
      },
      { path: 'admin', redirectTo: 'settings', pathMatch: 'full' },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [roleGuard],
        data: { module: 'profile' },
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
